import { format } from 'date-fns';
import type { TFunction } from 'i18next';

import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import { getDpiaBadgeClass, getDpiaLabel } from 'pages/ProcessingActivities/_shared/apaDpiaStatus.helpers';
import type { AssetRopaCapabilities } from 'pages/ProcessingActivities/_shared/assetRopaCapabilities';
import { getAdminDpiaNeedAssessmentAction } from 'pages/ProcessingActivities/_shared/dpiaCaseActions';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

export { getDpiaBadgeClass, getDpiaLabel };

export const formatProcessingActivityReviewDate = (value: string) => {
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return value;
  }
};

export const getReviewStatusDotClass = (status: AssetProcessingActivityReviewRow['reviewStatus'], styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'accepted':
      return styles.statusDotSuccess;
    case 'submitted':
      return styles.statusDotInfo;
    case 'clarification_requested':
      return styles.statusDotDanger;
    default:
      return styles.statusDotWarning;
  }
};

export const getUniqueRowMenuItems = (
  row: AssetProcessingActivityReviewRow,
  capabilities: AssetRopaCapabilities,
  handlers: {
    onAccept: (row: AssetProcessingActivityReviewRow) => void;
    onAssessDpia: (row: AssetProcessingActivityReviewRow) => void;
    onOpenAdminDpiaReview: (row: AssetProcessingActivityReviewRow) => void;
    onOpenOwnerEditor: (row: AssetProcessingActivityReviewRow, mode: 'view' | 'edit') => void;
    onRemove?: (row: AssetProcessingActivityReviewRow) => void;
    onRequestClarification: (row: AssetProcessingActivityReviewRow) => void;
  },
  t: TFunction,
) => {
  const needAssessmentAction = getAdminDpiaNeedAssessmentAction(row, capabilities.canAssessDpia);

  return [
    ...(capabilities.canOpenApaEditor && capabilities.canEditApa
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.actions.edit),
            onClick: () => handlers.onOpenOwnerEditor(row, 'edit'),
          },
        ]
      : []),
    ...(capabilities.canRemoveProcessingActivity && handlers.onRemove
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.owner_workspace.actions.remove_from_asset),
            onClick: () => handlers.onRemove?.(row),
          },
        ]
      : []),
    ...(capabilities.canAcceptRow && row.canAccept
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.actions.accept),
            onClick: () => handlers.onAccept(row),
          },
        ]
      : []),
    ...(capabilities.canRequestClarificationFromList && row.canRequestClarification
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.actions.request_clarification),
            onClick: () => handlers.onRequestClarification(row),
          },
        ]
      : []),
    ...(needAssessmentAction === 'reassess'
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.apa_review.start_reassessment),
            onClick: () => handlers.onAssessDpia(row),
          },
        ]
      : []),
    ...(needAssessmentAction === 'assess'
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.apa_review.assess_dpia),
            onClick: () => handlers.onAssessDpia(row),
          },
        ]
      : []),
    ...(capabilities.canOpenAdminDpiaReview && row.dpiaId
      ? [
          {
            label: t(processing_activities_messages.asset_ropa.apa_review.open_admin_dpia_review),
            onClick: () => handlers.onOpenAdminDpiaReview(row),
          },
        ]
      : []),
  ];
};
